import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
import { nextOverlayPage, overlayPercent, readOverlayKey, sameOverlayPosition } from './overlay_keyboard';
import { choiceQuestionLabel, resequenceChoiceOptions, YN_NA_OPTION_SPEC, } from '../../constants/consent_element_types.constants';
// Exact values from Figma's "Fields" component set (node 399:7783, file
// bT7upy29A7Oiu4pCHWC5i8) via get_design_context / get_variable_defs — the "_chiclet"
// marker uses `--color-active-100/600/900`, pixel-identical to nucleus's blue-100
// (#dbeafe) / blue-600 (#2563eb) / blue-900 (#1e3a8a). Same pair for checkbox and radio
// options alike, selected or not — this is not a per-type or per-selection color.
const OPTION_FILL = '#DBEAFE';
const OPTION_BORDER = '#2563EB';
const OPTION_TEXT = '#1E3A8A';
/**
 * A single response option of a SINGLE_SELECT / MULTI_SELECT question, rendered as a compact numbered
 * chiplet that is positioned independently on the PDF (SSP-4085). Radio options are round; checkbox
 * options are square. The visible content is only the integer key — no signer-visible text, so no
 * translation is required.
 */
export const ConsentChoiceOptionMarker = ({ element, option, isSelected, isTabStop = true, totalPages = 1, onSelect, onUpdate, onDeleteElement, onAnnounce = () => undefined, onItemRemoved = () => undefined, }) => {
    const translate = useLocalizeMessage();
    const containerRef = useRef(null);
    const dragStartRef = useRef(null);
    const isRadio = element.type === 'SINGLE_SELECT';
    const isYnDefault = isRadio && (element.config.ec__choice_preset ?? 'custom') === 'yn_default';
    // In the locked Yes/No preset, only the optional N/A option may be removed; Y and N are fixed.
    const canDelete = !isYnDefault || option.key === YN_NA_OPTION_SPEC.key;
    // Locked Yes/No/N-A preset shows its fixed value; custom radios and all checkboxes show the option number.
    const markerLabel = isYnDefault ? option.value : String(option.key);
    // What assistive technology announces for this response control. The number inside the chiplet is
    // meaningless on its own, so the name carries the recorded question followed by this option's
    // printed response text — both author-entered in the document's own language, so neither is
    // translated; only the fallback for an option with no text yet is (SSP-4085).
    const optionText = option.localizedAriaLabel?.trim() || option.value.trim() || translate('Option {key}', { key: String(option.key) });
    const questionText = choiceQuestionLabel(element.config);
    const accessibleName = questionText ? `${questionText}, ${optionText}` : optionText;
    const applyOptionUpdate = (nextOption) => {
        const options = (element.config.ec__options ?? []).map((o) => (o.id === nextOption.id ? nextOption : o));
        onUpdate({ ...element, config: { ...element.config, ec__options: options } });
    };
    const handleDeleteOption = () => {
        const remaining = (element.config.ec__options ?? []).filter((o) => o.id !== option.id);
        if (remaining.length === 0) {
            onDeleteElement(element.id);
            return;
        }
        // Keep custom option keys contiguous after a removal; the Yes/No preset keeps its fixed keys.
        const next = isYnDefault ? remaining : resequenceChoiceOptions(remaining);
        onUpdate({ ...element, config: { ...element.config, ec__options: next } });
        onItemRemoved();
    };
    const handleMouseDown = (e) => {
        if (e.target.closest('[data-action]')) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        onSelect(element.id);
        const parent = containerRef.current?.parentElement?.parentElement;
        if (!parent) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, pos: { ...option.position } };
        const onMouseMove = (moveEvent) => {
            if (!dragStartRef.current) {
                return;
            }
            const dx = (moveEvent.clientX - dragStartRef.current.mouseX) / parentRect.width;
            const dy = (moveEvent.clientY - dragStartRef.current.mouseY) / parentRect.height;
            const newX = Math.max(0, Math.min(1 - option.position.widthPct, dragStartRef.current.pos.xPct + dx));
            const newY = Math.max(0, Math.min(1 - option.position.heightPct, dragStartRef.current.pos.yPct + dy));
            applyOptionUpdate({ ...option, placed: true, position: { ...option.position, xPct: newX, yPct: newY } });
        };
        const onMouseUp = () => {
            dragStartRef.current = null;
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };
    const handleKeyDown = (e) => {
        const result = readOverlayKey(e.key, e, option.position, { widthPct: 0.01, heightPct: 0.01 });
        if (!result) {
            return;
        }
        if (result.kind === 'select') {
            e.preventDefault();
            onSelect(element.id);
            return;
        }
        if (result.kind === 'delete') {
            e.preventDefault();
            if (!canDelete) {
                onAnnounce(translate('{label} cannot be removed', { label: accessibleName }));
                return;
            }
            handleDeleteOption();
            onAnnounce(translate('{label} removed', { label: accessibleName }));
            return;
        }
        if (result.kind === 'resize') {
            e.preventDefault();
            onAnnounce(translate('Response options cannot be resized'));
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (result.kind === 'page') {
            const page = nextOverlayPage(option.page, result.delta, totalPages);
            if (page === null) {
                onAnnounce(result.delta < 0 ? translate('Already on the first page') : translate('Already on the last page'));
                return;
            }
            applyOptionUpdate({ ...option, page, placed: true });
            onAnnounce(translate('Moved to page {page}', { page }));
            return;
        }
        if (sameOverlayPosition(option.position, result.position)) {
            onAnnounce(translate('Cannot move further'));
            return;
        }
        applyOptionUpdate({ ...option, placed: true, position: result.position });
        onAnnounce(translate('Moved to {x} percent from the left, {y} percent from the top', {
            x: overlayPercent(result.position.xPct),
            y: overlayPercent(result.position.yPct),
        }));
    };
    return (_jsxs("div", { ref: containerRef, "data-testid": `consent-choice-option-${element.id}-${option.key}`, style: {
            position: 'absolute',
            left: `${option.position.xPct * 100}%`,
            top: `${option.position.yPct * 100}%`,
            width: `${option.position.widthPct * 100}%`,
            height: `${option.position.heightPct * 100}%`,
            cursor: 'grab',
            userSelect: 'none',
        }, onMouseDown: handleMouseDown, title: accessibleName, children: [_jsx("div", { role: 'button', tabIndex: isTabStop ? 0 : -1, "aria-label": accessibleName, "data-overlay-item": `opt-${element.id}-${option.id}`, onFocus: () => onSelect(element.id), onKeyDown: handleKeyDown, className: 'flex h-full w-full items-center justify-center overflow-hidden px-0.5 text-[10px] font-semibold leading-none', style: {
                    border: `${isSelected ? 2 : 1.5}px solid ${OPTION_BORDER}`,
                    borderRadius: isRadio ? '9999px' : '2px',
                    backgroundColor: OPTION_FILL,
                    color: OPTION_TEXT,
                }, children: markerLabel }), isSelected && canDelete && (_jsx("button", { type: 'button', tabIndex: -1, "data-action": 'delete-option', "data-testid": `delete-choice-option-${element.id}-${option.key}`, className: 'absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-700 text-[10px] leading-none text-white hover:bg-neutral-900', onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteOption();
                }, "aria-label": translate('Remove option'), title: translate('Remove option'), children: "\u00D7" }))] }));
};
